import { Results } from '../../models/unit.model';
import { IProductElement, OneOfElements } from '../../models/project.model';
import { ElementPropertyName } from '../../models/element-property.model';
import {
  ElementPath,
  isElement,
  isProductElement,
} from '../recursive-element.helpers';
import { getConversionFactorValue } from '../conversion-factors.helpers';
import { getBuildingLifetime } from '../project.helpers';
import {
  getCharacteristicsSbefCode,
  getSbefCodeFromElementOrRecipe,
  getSoleSbefKey,
} from '../sbef.helpers';
import { genericProductsLookup } from '../../generic-products';
import {
  getElementProperty,
  getElementPropertyResolvedCountByNameOrId,
  getResolvedCountInPath,
  hasCount,
} from '../element-property.helpers';
import { findMap } from '../array.helpers';
import { guards } from '../typeguards.helpers';
import { getGenericProductCharacteristics } from './generic-product-characteristics.helpers';
import { isUnderMaintenanceElement } from './maintenance.helpers';
import { MapResultsContext } from './map-results-context.model';

/**
 * Resolve the service life (years) used for B4 (IVL, see DEV-3324):
 * 1. Lifetime explicitly set by the user on the element (or an ancestor)
 * 2. Product `characteristics.lifetime` for the element's SBEF code
 * 3. Resolved lifetime from the path (SBEF table fallback, parent inheritance, default)
 */
export const getProductElementLifetime = (
  path: ElementPath,
  productElement: IProductElement,
): number => {
  // User-set lifetime always wins (explicit count, not category fallbackCount)
  const userSetLifetime = findMap(path.toReversed(), (el) => {
    const property = getElementProperty(el, ElementPropertyName.Lifetime);
    if (!hasCount(property)) return undefined;
    const resolved = getElementPropertyResolvedCountByNameOrId(
      el,
      ElementPropertyName.Lifetime,
    );
    return guards.is.number(resolved) && resolved > 0 ? resolved : undefined;
  });
  if (userSetLifetime) return userSetLifetime;

  // Product lifetime overrides the SBEF table; keyed by the element SBEF code or,
  // when unset, the product's sole lifetime SBEF key (e.g. windows keyed '55')
  const lifetimeByCode = getGenericProductCharacteristics(
    path,
    productElement,
  )?.lifetime;
  const sbefCode = getCharacteristicsSbefCode(
    productElement,
    path,
    lifetimeByCode,
  );
  const productLifetime = sbefCode ? lifetimeByCode?.[sbefCode] : undefined;
  if (productLifetime) return productLifetime;

  return getResolvedCountInPath(path, ElementPropertyName.Lifetime, 0);
};

/**
 * Product service-life override (years) for an element's `lifetime` property fallback,
 * surfaced as the `product_lifetime` expression variable so {@link getSBEFLifetime}
 * can rank it above the SBEF table and parent inheritance (display + pipeline).
 *
 * Mirrors the product step of {@link getProductElementLifetime} but reads the element's
 * direct product children (not descendants — those carry their own lifetime). Keyed by
 * the element SBEF code, or — when unset — the product's sole lifetime key (e.g. windows
 * keyed '55', see {@link getSoleSbefKey}).
 */
export const getElementProductLifetime = (
  element: OneOfElements,
): number | undefined => {
  // Only IElement has product children + an SBEF code to key by
  if (!isElement(element)) return undefined;

  const elementSbef = getSbefCodeFromElementOrRecipe(element);

  return findMap(element.elements, (child) => {
    if (!isProductElement(child)) return undefined;
    const genericId = child.generic_product_id ?? child.product_id;
    const lifetimeByCode = genericId
      ? genericProductsLookup[genericId]?.characteristics?.lifetime
      : undefined;
    if (!lifetimeByCode) return undefined;

    const sbefCode = elementSbef ?? getSoleSbefKey(lifetimeByCode);
    return sbefCode ? lifetimeByCode[sbefCode] : undefined;
  });
};

/**
 * Add lifetime (replacement) emissions to B4: the A-stage CO2e/cost repeated once
 * per additional service-life cycle within the building lifetime.
 */
export const mapResultsByLifetime = (
  results: Results,
  { project, path, productElement }: MapResultsContext,
): Results => {
  if (isUnderMaintenanceElement(path)) return results;

  const lifetime = getProductElementLifetime(path, productElement);
  const buildingLifetime = getBuildingLifetime(project);
  if (!lifetime || !buildingLifetime) {
    return results;
  }
  const lifecycles = Math.max(buildingLifetime / lifetime - 1, 0);
  if (lifecycles) {
    return {
      ...results,
      co2e_B4: getConversionFactorValue(results, 'co2e_A') * lifecycles,
      sek_B4: getConversionFactorValue(results, 'sek_A') * lifecycles,
    };
  }
  return results;
};
